export class FirebaseJsonUtils {

  //Converts full Admin Panel JSON to compact Firebase JSON format
  static toFirebaseJson(obj) {
    if (obj === null || obj === undefined) {
      return undefined;
    }

    // Handle primitive types (string, number, boolean)
    if (typeof obj !== 'object') {
      // Keep all primitives, including false and 0
      // These are intentionally set values
      return obj;
    }

    // Handle arrays
    if (Array.isArray(obj)) {
      // Recursively process array items
      const result = obj
        .map(item => this.toFirebaseJson(item))
        .filter(item => item !== undefined);

      // Return undefined only for completely empty arrays
      return result.length === 0 ? undefined : result;
    }

    // Handle objects
    const result = {};
    let hasContent = false;

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = this.toFirebaseJson(obj[key]);

        // Only include if value is not undefined
        // This will exclude empty arrays and empty objects
        if (value !== undefined) {
          result[key] = value;
          hasContent = true;
        }
      }
    }

    // Return undefined only for completely empty objects
    return hasContent ? result : undefined;
  }

  // Converts Firebase JSON to full Admin Panel JSON format by deserializing and re-serializing
  // This uses the serializr library's deserialize/serialize to restore the full schema
  static toAdminPanelJson(firebaseJson, modelType, deserialize, serialize) {
    try {
      // Deserialize Firebase JSON to model instance
      const modelInstance = deserialize(modelType, firebaseJson);

      // Serialize back to get full schema
      return serialize(modelType, modelInstance);
    } catch (error) {
      console.error('Error converting Firebase JSON to Admin Panel JSON:', error);
      throw error;
    }
  }

  // Builds a union template from an array of properly-structured items.
  // The template marks which fields should be arrays vs objects,
  // by merging the structure of all items.
  static buildTemplate(items) {
    if (!items || items.length === 0) return null;

    const template = {};

    const merge = (target, source) => {
      if (!source || typeof source !== 'object' || Array.isArray(source)) return;

      for (const key in source) {
        if (!source.hasOwnProperty(key)) continue;
        const val = source[key];

        if (Array.isArray(val)) {
          // Mark as array field; merge element structure if elements exist
          if (!target[key] || !target[key].__isArray) {
            target[key] = { __isArray: true, __elementTemplate: {} };
          }
          val.forEach(el => {
            if (el && typeof el === 'object' && !Array.isArray(el)) {
              merge(target[key].__elementTemplate, el);
            }
          });
        } else if (val && typeof val === 'object') {
          if (!target[key] || target[key].__isArray) {
            target[key] = target[key] || {};
          }
          if (!target[key].__isArray) {
            merge(target[key], val);
          }
        }
      }
    };

    items.forEach(item => merge(template, item));
    return template;
  }

  // Normalizes import data using a template built by buildTemplate().
  // Wraps non-array values in arrays where the template indicates arrays are expected.
  // Recursively processes nested objects and array elements.
  static normalizeWithTemplate(data, template) {
    if (!data || typeof data !== 'object' || Array.isArray(data) || !template) return data;

    const result = { ...data };
    for (const key in result) {
      if (!result.hasOwnProperty(key)) continue;
      const tmpl = template[key];
      if (!tmpl) continue;

      if (tmpl.__isArray) {
        // Template expects an array
        if (!Array.isArray(result[key])) {
          if (result[key] !== null && result[key] !== undefined) {
            // Wrap single value in array
            result[key] = [result[key]];
          } else {
            result[key] = [];
          }
        }
        // Recurse into array elements using the element template
        const elTmpl = tmpl.__elementTemplate;
        if (elTmpl && Object.keys(elTmpl).length > 0) {
          result[key] = result[key].map(el =>
            this.normalizeWithTemplate(el, elTmpl)
          );
        }
      } else if (typeof tmpl === 'object' && typeof result[key] === 'object' && !Array.isArray(result[key])) {
        // Both are objects — recurse
        result[key] = this.normalizeWithTemplate(result[key], tmpl);
      }
    }
    return result;
  }

  // Detects whether JSON is in Firebase format or Admin Panel format
  // Firebase format is more compact and doesn't include empty arrays/objects
  static detectFormat(json) {
    // Simple heuristic: Check for empty arrays/objects
    // Admin Panel JSON has many empty arrays and nested empty objects
    // Firebase JSON has very few or no empty arrays/objects

    if (!json || typeof json !== 'object') {
      return 'unknown';
    }

    let emptyArrayCount = 0;
    let totalArrays = 0;
    let totalFields = 0;

    const countEmpties = (obj, depth = 0) => {
      if (depth > 10) return; // Prevent deep recursion

      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          totalFields++;
          const value = obj[key];

          if (Array.isArray(value)) {
            totalArrays++;
            if (value.length === 0) {
              emptyArrayCount++;
            } else {
              // Check array items recursively
              value.forEach(item => {
                if (item && typeof item === 'object') {
                  countEmpties(item, depth + 1);
                }
              });
            }
          } else if (value && typeof value === 'object') {
            countEmpties(value, depth + 1);
          }
        }
      }
    };

    countEmpties(json);

    // If more than 20% of arrays are empty, likely Admin Panel format
    // Firebase format should have almost no empty arrays
    if (totalArrays > 0) {
      const emptyArrayRatio = emptyArrayCount / totalArrays;
      return emptyArrayRatio > 0.2 ? 'admin-panel' : 'firebase';
    }

    // Fallback: Firebase format tends to be more compact
    // If we can't determine from empty arrays, assume firebase
    return 'firebase';
  }
}
